import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { cobrosDeRemito } from '../utils/cobroMixto';
import { toast } from 'sonner';
import {
  BanknotesIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  BuildingLibraryIcon,
  DocumentTextIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { disconnectAppSocket, getAppSocket } from '../lib/wesSocket';
import { promptDialog } from '../components/PromptDialog';
import { toYMD } from '../utils/fechaLocal';

// Tipos de egreso (categorías) para el select con búsqueda
const EXPENSE_TYPE_OPTIONS = [
  { value: 'operating', label: 'Gastos Operativos' },
  { value: 'providers', label: 'Pago a Proveedores' },
  { value: 'salaries', label: 'Salarios' },
  { value: 'taxes', label: 'Impuestos' },
  { value: 'ownerWithdrawals', label: 'Retiros del dueño' },
  { value: 'other', label: 'Otros Gastos' }
];

// Sugerencias de "tipo de gasto" (creatable: se puede elegir o escribir uno propio)
const EXPENSE_CONCEPT_OPTIONS = [
  'Combustible', 'Comida / Viáticos', 'Servicios (luz/agua/gas)', 'Internet / Teléfono',
  'Alquiler', 'Mantenimiento', 'Limpieza', 'Papelería / Oficina', 'Fletes / Envíos',
  'Publicidad / Marketing', 'Herramientas', 'Repuestos / Insumos', 'Impuestos / Tasas',
  'Honorarios', 'Banco / Comisiones', 'Sueldos', 'Retiro del dueño'
].map((c) => ({ value: c, label: c }));

interface CashStats {
  currentCash: number;
  todayIncome: {
    total: number;
    cash: number;
    creditCard: number;
    debitCard: number;
    transfer: number;
    check: number;
    qr: number;
    currentAccount: number;
    crypto: number;
    other: number;
  };
  // Ventas del dia (incluye todas las formas de pago, incluso cuenta corriente)
  salesTodayTotal: number;
  todayExpenses: {
    total: number;
    providers: number;
    operating: number;
    salaries: number;
    taxes: number;
    ownerWithdrawals: number;
    other: number;
  };
  weeklyComparison: {
    thisWeek: number;
    lastWeek: number;
    growth: number;
  };
  monthlyComparison: {
    thisMonth: number;
    lastMonth: number;
    growth: number;
  };
}

interface CashMovement {
  id: string;
  type: 'income' | 'expense';
  amount: number;
  methodKey: string;
  method: string;
  description: string;
  time: string;
  reference?: string;
}

interface CashClosure {
  id: string;
  date: string;
  countedCash: number;
  theoreticalCash: number;
  difference: number;
  withdrawnCash?: number;
  remainingCash?: number | null;
  closedAt?: string;
  notes?: string | null;
}

const CashRegister: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const navigate = useNavigate();
  const [stats, setStats] = useState<CashStats | null>(null);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  // Resumen de efectivo (fondo fijo + ventas ef. + cobranzas cta cte ef. − egresos ef.)
  const [cashSummary, setCashSummary] = useState<any | null>(null);   // del período seleccionado
  const [todaySummary, setTodaySummary] = useState<any | null>(null); // de HOY (para el arqueo/cierre)
  const [loading, setLoading] = useState(true);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    type: 'operating',
    amount: '',
    description: '',
    method: 'efectivo',
    accountId: ''
  });
  // Cuentas propias (tesorería) para elegir de dónde sale la plata en egresos electrónicos
  const [financialAccounts, setFinancialAccounts] = useState<Array<{ id: string; name: string; kind?: string }>>([]);
  // Medios que salen de una cuenta propia (no efectivo)
  const EXPENSE_NEEDS_ACCOUNT = ['transferencia', 'tarjeta_debito', 'qr', 'cripto'];

  useEffect(() => {
    axios.get('/api/financial-accounts')
      .then(r => setFinancialAccounts(r.data.accounts || []))
      .catch(() => {});
  }, []);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [cashClosure, setCashClosure] = useState<CashClosure | null>(null);
  // Cierre del día seleccionado en el filtro (para ver arqueos de días anteriores)
  const [periodClosure, setPeriodClosure] = useState<CashClosure | null>(null);
  const [periodRange, setPeriodRange] = useState<{ from: string; to: string } | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [countedCash, setCountedCash] = useState('');
  const [withdrawnCash, setWithdrawnCash] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [closingCash, setClosingCash] = useState(false);
  const [reopeningCash, setReopeningCash] = useState(false);
  const [pendingSummary, setPendingSummary] = useState({
    currentAccounts: { count: 0, total: 0 },
    checks: { count: 0, total: 0 },
    remitosUnpaid: { count: 0, total: 0 },
  });

  // UX: filtros de fecha y metodo
  type DateRangeType = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'custom';
  const [rangeType, setRangeType] = useState<DateRangeType>('today');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const refreshTimeoutRef = useRef<number | null>(null);
  const fetchCashDataRef = useRef<null | ((options?: { silent?: boolean }) => void)>(null);

  // Atajo de teclado para abrir el modal de egreso (N)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Solo activar si no estamos escribiendo en un input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (event.key === 'n' || event.key === 'N') {
        event.preventDefault();
        setShowExpenseModal(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const initialLoadRef = useRef(true);
  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchCashData();
    } else {
      // Cambios de filtro (periodo / metodo de pago): refresco silencioso, sin spinner full-page
      fetchCashData({ silent: true });
    }
  }, [rangeType, customFrom, customTo, methodFilter]);

  const scheduleSilentRefresh = () => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = window.setTimeout(() => {
      fetchCashDataRef.current?.({ silent: true });
    }, 350);
  };

  useEffect(() => {
    const socket = getAppSocket();
    const handleCashUpdate = () => scheduleSilentRefresh();
    const handleConnect = () => scheduleSilentRefresh();

    socket.on('cash:update', handleCashUpdate);
    socket.on('connect', handleConnect);

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      socket.off('cash:update', handleCashUpdate);
      socket.off('connect', handleConnect);
      disconnectAppSocket();
    };
  }, []);

  const getRangeLabel = (): string => {
    switch (rangeType) {
      case 'today': return 'Hoy';
      case 'yesterday': return 'Ayer';
      case 'thisWeek': return 'Semana actual';
      case 'thisMonth': return 'Mes actual';
      case 'custom': {
        if (customFrom && customTo) return `${new Date(customFrom).toLocaleDateString('es-AR')} - ${new Date(customTo).toLocaleDateString('es-AR')}`;
        return 'Periodo personalizado';
      }
      default: return '';
    }
  };

  const normalizePaymentMethod = (paymentMethod?: string): string => {
    const raw = String(paymentMethod || '').trim().toLowerCase();
    if (!raw) return 'other';
    if (raw === 'efectivo') return 'efectivo';
    if (raw === 'debito' || raw === 'tarjeta_debito' || raw === 'tarjeta debito' || raw === 'tarjeta débito') return 'tarjeta_debito';
    if (raw === 'tarjeta_3' || raw === 'tarjeta_6' || raw === 'credito' || raw === 'tarjeta_credito' || raw === 'tarjeta credito' || raw === 'tarjeta crédito') return 'tarjeta_credito';
    if (raw === 'transferencia') return 'transferencia';
    if (raw === 'cheque') return 'cheque';
    if (raw === 'cuenta_corriente' || raw === 'cuenta corriente' || raw === 'cta_cte' || raw === 'cta cte') return 'cuenta_corriente';
    if (raw === 'qr') return 'qr';
    if (raw === 'cripto' || raw === 'crypto') return 'cripto';
    return 'other';
  };

  const getPaymentMethodLabelByKey = (methodKey: string): string => {
    switch (methodKey) {
      case 'efectivo': return 'Efectivo';
      case 'tarjeta_debito': return 'Tarjeta Debito';
      case 'tarjeta_credito': return 'Tarjeta Credito';
      case 'transferencia': return 'Transferencia';
      case 'cheque': return 'Cheque';
      case 'cuenta_corriente': return 'Cta Cte';
      case 'qr': return 'QR';
      case 'cripto': return 'Cripto';
      default: return 'Otro';
    }
  };

  const computeFromRemitos = (remitos: any[]): { computedStats: CashStats; computedMovements: CashMovement[] } => {
    const todayIncome = { cash: 0, creditCard: 0, debitCard: 0, transfer: 0, check: 0, qr: 0, currentAccount: 0, crypto: 0, other: 0 } as any;
    const salesTotal = remitos.reduce((sum, r) => sum + (r.estimatedValue || 0), 0);

    const paymentLabel = (pm?: string) => {
      switch (pm) {
        case 'efectivo': return 'Efectivo';
        case 'debito': return 'Tarjeta Debito';
        case 'tarjeta_3': return 'Tarjeta Credito (3)';
        case 'tarjeta_6': return 'Tarjeta Credito (6)';
        case 'transferencia': return 'Transferencia';
        case 'cheque': return 'Cheque';
        case 'cuenta_corriente': return 'Cuenta Corriente';
        default: return 'Otro';
      }
    };

    // Solo mostrar como ingresos los que realmente ingresan a caja (excluir Cuenta Corriente)
    // Un cobro mixto entra como un movimiento por medio de pago (su parte, sin recargo)
    const movements: CashMovement[] = remitos
      .sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .flatMap((r) => {
        const cobros = cobrosDeRemito(r, r.estimatedValue || 0);
        return cobros.map((c, i) => {
          const methodKey = normalizePaymentMethod(c.method);
          return {
            id: cobros.length > 1 ? `${r.id}-${i}` : r.id,
            type: 'income' as const,
            amount: c.amount,
            methodKey,
            method: getPaymentMethodLabelByKey(methodKey),
            description: `Remito ${r.remitNumber} - ${r.clientName}${cobros.length > 1 ? ' (pago mixto)' : ''}`,
            time: new Date(r.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
            reference: r.remitNumber,
          };
        });
      });

    // Sumar ingresos reales (excluye cuenta corriente); el mixto suma cada parte en su medio
    remitos.forEach((r) => cobrosDeRemito(r, r.estimatedValue || 0).forEach((c) => {
      const amt = c.amount;
      switch (normalizePaymentMethod(c.method)) {
        case 'efectivo': todayIncome.cash += amt; break;
        case 'tarjeta_debito': todayIncome.debitCard += amt; break;
        case 'tarjeta_credito': todayIncome.creditCard += amt; break;
        case 'transferencia': todayIncome.transfer += amt; break;
        case 'cheque': todayIncome.check += amt; break;
        case 'qr': todayIncome.qr += amt; break;
        case 'cuenta_corriente': todayIncome.currentAccount += amt; break;
        case 'cripto': todayIncome.crypto += amt; break;
        default: todayIncome.other += amt; break;
      }
    }));

    const totalIncome = todayIncome.cash + todayIncome.creditCard + todayIncome.debitCard + todayIncome.transfer + todayIncome.check + todayIncome.qr + todayIncome.crypto + todayIncome.other;

    const computedStats: CashStats = {
      currentCash: totalIncome - 0, // sin egresos calculados por ahora
      todayIncome: {
        total: totalIncome,
        cash: todayIncome.cash,
        creditCard: todayIncome.creditCard,
        debitCard: todayIncome.debitCard,
        transfer: todayIncome.transfer,
        check: todayIncome.check,
        qr: todayIncome.qr,
        currentAccount: todayIncome.currentAccount,
        crypto: todayIncome.crypto,
        other: todayIncome.other,
      },
      salesTodayTotal: salesTotal,
      todayExpenses: { total: 0, providers: 0, operating: 0, salaries: 0, taxes: 0, ownerWithdrawals: 0, other: 0 },
      weeklyComparison: { thisWeek: totalIncome, lastWeek: 0, growth: 0 },
      monthlyComparison: { thisMonth: totalIncome, lastMonth: 0, growth: 0 },
    };

    return { computedStats, computedMovements: movements };
  };

  // Helpers para comparaciones
  const formatYMD = (d: Date): string => toYMD(d);
  const sumIncome = (remitos: any[]): number =>
    remitos
      .flatMap((r) => cobrosDeRemito(r, r.estimatedValue || 0))
      .filter((c) => normalizePaymentMethod(c.method) !== 'cuenta_corriente')
      .reduce((sum, c) => sum + (c.amount || 0), 0);

  const getCurrentWeekRange = (): { from: string; to: string } => {
    const to = new Date();
    const day = to.getDay(); // 0 domingo, 1 lunes
    const diff = (day === 0 ? 6 : day - 1);
    const from = new Date(to);
    from.setDate(to.getDate() - diff);
    return { from: formatYMD(from), to: formatYMD(to) };
  };

  const getPreviousWeekRange = (): { from: string; to: string } => {
    const { from } = getCurrentWeekRange();
    const end = new Date(from);
    end.setDate(end.getDate() - 1);
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    return { from: formatYMD(start), to: formatYMD(end) };
  };

  const getCurrentMonthRange = (): { from: string; to: string } => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: formatYMD(from), to: formatYMD(now) };
  };

  const getPreviousMonthRange = (): { from: string; to: string } => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0); // ultimo dia mes anterior
    const from = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1);
    return { from: formatYMD(from), to: formatYMD(prevMonth) };
  };

  const fetchCashData = async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) {
        setLoading(true);
      }
      // Calcular fechas segun filtro
      const getRangeDates = (): { from: string; to: string } => {
        const toDate = new Date();
        let fromDate = new Date();
        switch (rangeType) {
          case 'yesterday': {
            const d = new Date();
            d.setDate(d.getDate() - 1);
            const ymd = toYMD(d);
            return { from: ymd, to: ymd };
          }
          case 'thisWeek': {
            const d = new Date();
            const day = d.getDay();
            const diff = (day === 0 ? 6 : day - 1); // lunes como inicio
            fromDate.setDate(d.getDate() - diff);
            break;
          }
          case 'thisMonth': {
            fromDate = new Date(toDate.getFullYear(), toDate.getMonth(), 1);
            break;
          }
          case 'custom': {
            if (customFrom && customTo) return { from: customFrom, to: customTo };
            const y = toYMD(toDate);
            return { from: y, to: y };
          }
          default: {
            // today
            const y = toYMD(toDate);
            return { from: y, to: y };
          }
        }
        const from = toYMD(fromDate);
        const to = toYMD(toDate);
        return { from, to };
      };
      const { from, to } = getRangeDates();

      try {
        // Intentar obtener remitos emitidos de hoy y calcular ingresos
        const res = await axios.get(`/api/remitos`, {
          params: {
            status: 'issued',
            dateFrom: from,
            dateTo: to,
            limit: 1000,
          },
        });
        const remitos = res.data.remitos || [];
        const { computedStats, computedMovements } = computeFromRemitos(remitos);

        setPeriodRange({ from, to });
        // Cierre del día seleccionado (solo aplica a períodos de un día)
        if (from === to) {
          try {
            const pcRes = await axios.get(`/api/cash-closures/by-date/${from}`);
            setPeriodClosure(pcRes.data?.closure || null);
          } catch { setPeriodClosure(null); }
        } else {
          setPeriodClosure(null);
        }

        // Resumen de efectivo del período (fondo + ventas ef. + cobranzas − egresos)
        let summary: any = null;
        try {
          const sRes = await axios.get('/api/cash-movements/summary', { params: { from, to } });
          summary = sRes.data;
          setCashSummary(summary);
          const todayYmd = formatYMD(new Date());
          if (from === todayYmd && to === todayYmd) {
            setTodaySummary(summary);
          } else {
            const tRes = await axios.get('/api/cash-movements/summary', { params: { from: todayYmd, to: todayYmd } });
            setTodaySummary(tRes.data);
          }
        } catch { /* resumen no disponible: se muestran solo ventas */ }

        // Egresos reales del período → desglose por categoría
        if (summary) {
          const exp = { total: 0, providers: 0, operating: 0, salaries: 0, taxes: 0, ownerWithdrawals: 0, other: 0 };
          for (const e of (summary.egresos || [])) {
            const amt = Number(e.amount) || 0;
            exp.total += amt;
            switch (e.category) {
              case 'proveedores': exp.providers += amt; break;
              case 'gasto_operativo': exp.operating += amt; break;
              case 'sueldos': exp.salaries += amt; break;
              case 'impuestos': exp.taxes += amt; break;
              case 'retiro_dueno': exp.ownerWithdrawals += amt; break;
              default: exp.other += amt; break;
            }
          }
          computedStats.todayExpenses = exp;
        }

        // Cobranzas de cta cte en efectivo y egresos → también a la lista de movimientos
        const CAT_LABEL: Record<string, string> = {
          proveedores: 'Proveedores', gasto_operativo: 'Gasto operativo', sueldos: 'Sueldos',
          impuestos: 'Impuestos', retiro_dueno: 'Retiro del dueño', otro: 'Otro'
        };
        const extraMovements: CashMovement[] = [];
        if (summary) {
          for (const c of (summary.cobranzas || [])) {
            extraMovements.push({
              id: `cc-${c.id}`, type: 'income', amount: Number(c.amount) || 0,
              methodKey: 'efectivo', method: 'Efectivo',
              description: `Cobranza cta cte - ${c.entidad}${c.description ? ` (${c.description})` : ''}`,
              time: new Date(c.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
            } as CashMovement);
          }
          for (const e of (summary.egresos || [])) {
            const mk = normalizePaymentMethod(e.paymentMethod);
            extraMovements.push({
              id: `eg-${e.id}`, type: 'expense', amount: Number(e.amount) || 0,
              methodKey: mk, method: getPaymentMethodLabelByKey(mk),
              description: `${CAT_LABEL[e.category] || 'Egreso'} - ${e.description || ''}`,
              time: new Date(e.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
            } as CashMovement);
          }
        }
        const allMovements = [...extraMovements, ...computedMovements];

        // Comparación semanal
        try {
          const currWeek = getCurrentWeekRange();
          const prevWeek = getPreviousWeekRange();
          const [cwRes, pwRes] = await Promise.all([
            axios.get('/api/remitos', { params: { status: 'issued', dateFrom: currWeek.from, dateTo: currWeek.to, limit: 1000 } }),
            axios.get('/api/remitos', { params: { status: 'issued', dateFrom: prevWeek.from, dateTo: prevWeek.to, limit: 1000 } })
          ]);
          const weekThis = sumIncome(cwRes.data.remitos || []);
          const weekPrev = sumIncome(pwRes.data.remitos || []);
          const weekGrowth = weekPrev > 0 ? ((weekThis - weekPrev) / weekPrev) * 100 : 0;
          computedStats.weeklyComparison = { thisWeek: weekThis, lastWeek: weekPrev, growth: weekGrowth } as any;
        } catch {}

        // Comparación mensual
        try {
          const currMonth = getCurrentMonthRange();
          const prevMonth = getPreviousMonthRange();
          const [cmRes, pmRes] = await Promise.all([
            axios.get('/api/remitos', { params: { status: 'issued', dateFrom: currMonth.from, dateTo: currMonth.to, limit: 1000 } }),
            axios.get('/api/remitos', { params: { status: 'issued', dateFrom: prevMonth.from, dateTo: prevMonth.to, limit: 1000 } })
          ]);
          const monthThis = sumIncome(cmRes.data.remitos || []);
          const monthPrev = sumIncome(pmRes.data.remitos || []);
          const monthGrowth = monthPrev > 0 ? ((monthThis - monthPrev) / monthPrev) * 100 : 0;
          computedStats.monthlyComparison = { thisMonth: monthThis, lastMonth: monthPrev, growth: monthGrowth } as any;
        } catch {}

        setStats(computedStats);

        // Aplicar filtro de metodo a los movimientos visibles
        const filtered = methodFilter === 'all'
          ? allMovements
          : allMovements.filter(m => m.methodKey === methodFilter);
        setMovements(filtered);
        setLastUpdated(new Date());

        const loadPending = async () => {
          const [closureRes, accountsRes, checksRes, remitosIssuedRes] = await Promise.allSettled([
            axios.get('/api/cash-closures/today'),
            axios.get('/api/current-accounts', { params: { hasDebt: true, entityType: 'client' } }),
            axios.get('/api/checks', { params: { type: 'received', status: 'pending' } }),
            axios.get('/api/remitos', { params: { status: 'issued' } }),
          ]);

          const closure = closureRes.status === 'fulfilled' ? closureRes.value.data?.closure : null;
          const accounts = accountsRes.status === 'fulfilled' ? accountsRes.value.data.accounts || [] : [];
          const checks = checksRes.status === 'fulfilled' ? checksRes.value.data.checks || [] : [];
          const issuedRemitos = remitosIssuedRes.status === 'fulfilled' ? remitosIssuedRes.value.data.remitos || [] : [];

          const currentAccountsTotal = accounts.reduce((sum: number, acc: any) => sum + Number(acc.currentBalance || 0), 0);
          const checksTotal = checks.reduce((sum: number, chk: any) => sum + Number(chk.amount || 0), 0);
          const remitosUnpaid = issuedRemitos.filter((r: any) => !r.paidAt);
          const remitosUnpaidTotal = remitosUnpaid.reduce((sum: number, r: any) => {
            const amount = Number(r.totalContado || r.estimatedValue || 0);
            return sum + amount;
          }, 0);

          setPendingSummary({
            currentAccounts: { count: accounts.length, total: currentAccountsTotal },
            checks: { count: checks.length, total: checksTotal },
            remitosUnpaid: { count: remitosUnpaid.length, total: remitosUnpaidTotal },
          });

          setCashClosure(closure || null);
        };

        loadPending();
      } catch (apiErr) {
        // Fallback al mock si falla la API
        setStats({
          currentCash: 52430.5,
          todayIncome: { total: 11850, cash: 8200, creditCard: 2100, debitCard: 1350, transfer: 200, check: 0, qr: 0, currentAccount: 0, crypto: 0, other: 0 },
          salesTodayTotal: 11850,
          todayExpenses: { total: 5200, providers: 3500, operating: 1200, salaries: 0, taxes: 0, ownerWithdrawals: 0, other: 500 },
          weeklyComparison: { thisWeek: 67850, lastWeek: 58400, growth: 16.2 },
          monthlyComparison: { thisMonth: 245600, lastMonth: 198300, growth: 23.9 },
        });
        setMovements([
          { id: '1', type: 'income', amount: 1500, methodKey: 'efectivo', method: 'Efectivo', description: 'Cambio de neumaticos - Cliente Juan Perez', time: '14:30' },
        ]);
        setLastUpdated(new Date());
      }

    } catch (error) {
      if (!silent) {
        toast.error('Error al cargar datos de caja');
      } else {
        console.error('Error al cargar datos de caja:', error);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };
  fetchCashDataRef.current = fetchCashData;

  const getClosureDeadline = () => {
    const now = new Date();
    const day = now.getDay();
    if (day >= 1 && day <= 5) {
      return { hour: 18, minute: 30 };
    }
    if (day === 6) {
      return { hour: 13, minute: 0 };
    }
    return null;
  };

  const isClosureRequiredNow = () => {
    const deadline = getClosureDeadline();
    if (!deadline) return false;
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(deadline.hour, deadline.minute, 0, 0);
    return now >= cutoff;
  };

  // El arqueo controla SOLO efectivo: fondo inicial + ventas ef. + cobranzas ef. − egresos ef.
  // theoreticalCash = de HOY (para el modal de cierre).
  const theoreticalCash = todaySummary?.efectivoTeorico ?? 0;

  // Control de caja: sigue al día seleccionado en el filtro (Hoy/Ayer/día puntual).
  const todayYmdStr = formatYMD(new Date());
  const isSingleDay = periodRange ? periodRange.from === periodRange.to : true;
  const viewingToday = !periodRange || (isSingleDay && periodRange.from === todayYmdStr);
  const arqueoClosure = viewingToday ? cashClosure : periodClosure;
  const arqueoDayLabel = viewingToday
    ? 'hoy'
    : (isSingleDay && periodRange ? new Date(`${periodRange.from}T00:00:00`).toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' }) : '');
  // Si el día está cerrado, mostrar el teórico congelado del cierre; si no, el vivo del período
  const arqueoTeorico = arqueoClosure
    ? Number(arqueoClosure.theoreticalCash)
    : (viewingToday ? theoreticalCash : (cashSummary?.efectivoTeorico ?? 0));
  const realCash = arqueoClosure?.countedCash ?? null;
  const difference = arqueoClosure ? Number(arqueoClosure.difference) : (realCash === null ? null : Number((realCash - arqueoTeorico).toFixed(2)));

  // Mapeo de categorías del form → categorías del server (cash_movements)
  const EXPENSE_CATEGORY_TO_SERVER: Record<string, string> = {
    providers: 'proveedores',
    operating: 'gasto_operativo',
    salaries: 'sueldos',
    taxes: 'impuestos',
    ownerWithdrawals: 'retiro_dueno',
    other: 'otro'
  };

  const handleRegisterExpense = async () => {
    try {
      if (!expenseForm.amount || !expenseForm.description) {
        toast.error('Complete todos los campos requeridos');
        return;
      }

      const amount = parseFloat(expenseForm.amount);
      if (isNaN(amount) || amount <= 0) {
        toast.error('El monto debe ser un numero valido');
        return;
      }

      await axios.post('/api/cash-movements', {
        category: EXPENSE_CATEGORY_TO_SERVER[expenseForm.type] || 'otro',
        amount,
        description: expenseForm.description,
        paymentMethod: expenseForm.method,
        accountId: EXPENSE_NEEDS_ACCOUNT.includes(expenseForm.method) ? (expenseForm.accountId || null) : null
      });

      toast.success('Egreso registrado exitosamente');
      setShowExpenseModal(false);
      setExpenseForm({ type: 'operating', amount: '', description: '', method: 'efectivo', accountId: '' });
      fetchCashDataRef.current?.({ silent: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Error al registrar el egreso');
    }
  };

  // Editar el fondo fijo del cajón (solo admin)
  const editFixedFund = async () => {
    const v = await promptDialog({
      title: 'Fondo fijo del cajón',
      description: 'La plata de cambio con la que arranca el cajón. Si el último cierre registró un retiro, el teórico arranca de lo que quedó en el cajón; si no, de este fondo fijo.',
      defaultValue: String(cashSummary?.fondoFijo ?? 0),
      placeholder: '50000'
    });
    if (v === null) return;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) { toast.error('Monto inválido'); return; }
    try {
      await axios.put('/api/cash-movements/fixed-fund', { fixedFund: n });
      toast.success('Fondo fijo actualizado');
      fetchCashDataRef.current?.({ silent: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo actualizar el fondo fijo');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (normalizePaymentMethod(method)) {
      case 'efectivo':
        return BanknotesIcon;
      case 'tarjeta_credito':
      case 'tarjeta_debito':
        return CreditCardIcon;
      case 'transferencia':
        return BuildingLibraryIcon;
      case 'cheque':
        return DocumentTextIcon;
      case 'cuenta_corriente':
        return UsersIcon;
      case 'qr':
      case 'cripto':
      default:
        return CurrencyDollarIcon;
    }
  };

  const getPaymentMethodColor = (method: string) => {
    switch (normalizePaymentMethod(method)) {
      case 'efectivo':
        return 'bg-green-500';
      case 'tarjeta_credito':
        return 'bg-purple-500';
      case 'tarjeta_debito':
        return 'bg-blue-500';
      case 'transferencia':
        return 'bg-orange-500';
      case 'cheque':
        return 'bg-amber-500';
      case 'cuenta_corriente':
        return 'bg-slate-500';
      case 'qr':
        return 'bg-cyan-500';
      case 'cripto':
        return 'bg-fuchsia-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getDifferenceBadge = (value: number | null) => {
    if (value === null) return 'bg-gray-100 text-gray-800';
    if (value === 0) return 'bg-green-100 text-green-800';
    if (value < 0) return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const handleCloseCash = async () => {
    if (!stats) return;
    if (!todaySummary) {
      toast.error('No se pudo calcular el efectivo teórico de hoy. Recargá la página.');
      return;
    }
    const parsed = Number(countedCash);
    if (Number.isNaN(parsed)) {
      toast.error('Ingresa un monto contado valido');
      return;
    }

    const parsedWithdrawn = withdrawnCash === '' ? 0 : Number(withdrawnCash);
    if (Number.isNaN(parsedWithdrawn) || parsedWithdrawn < 0) {
      toast.error('Ingresa un retiro valido');
      return;
    }
    if (parsedWithdrawn > parsed) {
      toast.error('No podés retirar más de lo que contaste en el cajón');
      return;
    }

    try {
      setClosingCash(true);
      const payload = {
        date: formatYMD(new Date()),
        countedCash: parsed,
        theoreticalCash: theoreticalCash,
        withdrawnCash: parsedWithdrawn,
        notes: closeNotes
      };
      const response = await axios.post('/api/cash-closures/close', payload, { timeout: 15000 });
      setCashClosure(response.data.closure || null);
      setShowCloseModal(false);
      setCountedCash('');
      setWithdrawnCash('');
      setCloseNotes('');
      toast.success('Caja cerrada correctamente');
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('La caja ya esta cerrada para hoy');
      } else {
        toast.error(error?.response?.data?.error || 'No se pudo cerrar la caja');
      }
    } finally {
      setClosingCash(false);
    }
  };

  const handleReopenCash = async () => {
    if (!cashClosure) return;
    try {
      setReopeningCash(true);
      const payload = { date: cashClosure.date || formatYMD(new Date()) };
      await axios.post('/api/cash-closures/reopen', payload, { timeout: 15000 });
      setCashClosure(null);
      toast.success('Caja reabierta correctamente');
    } catch (error) {
      toast.error('No se pudo reabrir la caja');
    } finally {
      setReopeningCash(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="p-6 rounded-lg">
         <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-4">
           <div>
             <h1 className="text-2xl font-semibold text-foreground whitespace-nowrap">Estado de Caja</h1>
           </div>
           <div className="flex w-full md:w-auto flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
             <Button
               variant="outline"
               size="sm"
               className="h-9 w-full sm:w-auto"
              onClick={() => setShowCloseModal(true)}
              disabled={Boolean(cashClosure)}
            >
              <ClockIcon className="h-4 w-4 mr-2" />
              {cashClosure ? 'Caja cerrada' : 'Cerrar caja'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-full sm:w-auto"
              onClick={() => navigate('/cash-register/checks')}
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Seguimiento de cheques
            </Button>
            <Button
              onClick={() => setShowExpenseModal(true)}
              className="inline-flex h-9 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-black px-3 text-sm font-medium text-white shadow-xs hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90 sm:w-auto"
              size="sm"
            >
              <PlusIcon className="h-4 w-4" />
              Registrar Egreso
              <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
                N
              </kbd>
            </Button>
          </div>
        </div>
        {isClosureRequiredNow() && !cashClosure && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span>La caja de hoy sigue abierta. Es necesario cerrar caja.</span>
            <Button
              className="h-8 w-full md:w-auto"
              onClick={() => setShowCloseModal(true)}
            >
              Cerrar caja
            </Button>
          </div>
        )}
        {cashClosure && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span>
              Caja cerrada hoy. Cierre registrado {cashClosure.closedAt ? `a las ${new Date(cashClosure.closedAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}` : ''}.
            </span>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-full md:w-auto"
                onClick={handleReopenCash}
                disabled={reopeningCash}
              >
                {reopeningCash ? 'Reabriendo...' : 'Reabrir caja'}
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
          {/* Filtros de fecha */}
          <Tabs
            value={rangeType}
            onValueChange={(value) => setRangeType(value as DateRangeType)}
            className="w-full md:w-auto"
          >
            <TabsList className="w-full flex-wrap justify-start gap-1">
              <TabsTrigger value="today" className="px-2 text-xs sm:px-3 sm:text-sm">Hoy</TabsTrigger>
              <TabsTrigger value="yesterday" className="px-2 text-xs sm:px-3 sm:text-sm">Ayer</TabsTrigger>
              <TabsTrigger value="thisWeek" className="px-2 text-xs sm:px-3 sm:text-sm">Semana</TabsTrigger>
              <TabsTrigger value="thisMonth" className="px-2 text-xs sm:px-3 sm:text-sm">Mes</TabsTrigger>
              <TabsTrigger value="custom" className="px-2 text-xs sm:px-3 sm:text-sm">Personalizado</TabsTrigger>
            </TabsList>
          </Tabs>

          {rangeType === 'custom' && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <DatePicker
                value={customFrom}
                onChange={setCustomFrom}
                className="w-full sm:w-auto"
              />
              <span className="text-muted-foreground">-</span>
              <DatePicker
                value={customTo}
                onChange={setCustomTo}
                className="w-full sm:w-auto"
              />
            </div>
          )}

          {/* Filtros de metodo */}
          <Tabs
            value={methodFilter}
            onValueChange={(value) => setMethodFilter(value)}
            className="w-full md:w-auto"
          >
            <TabsList className="w-full flex-wrap justify-start gap-1">
              <TabsTrigger value="all" className="px-2 text-xs sm:px-3 sm:text-sm">Todos</TabsTrigger>
              <TabsTrigger value="efectivo" className="px-2 text-xs sm:px-3 sm:text-sm">Efectivo</TabsTrigger>
              <TabsTrigger value="tarjeta_debito" className="px-2 text-xs sm:px-3 sm:text-sm">Tarj. Debito</TabsTrigger>
              <TabsTrigger value="tarjeta_credito" className="px-2 text-xs sm:px-3 sm:text-sm">Tarj. Credito</TabsTrigger>
              <TabsTrigger value="transferencia" className="px-2 text-xs sm:px-3 sm:text-sm">Transferencia</TabsTrigger>
              <TabsTrigger value="cheque" className="px-2 text-xs sm:px-3 sm:text-sm">Cheque</TabsTrigger>
              <TabsTrigger value="qr" className="px-2 text-xs sm:px-3 sm:text-sm">QR</TabsTrigger>
              <TabsTrigger value="cuenta_corriente" className="px-2 text-xs sm:px-3 sm:text-sm">Cta Cte</TabsTrigger>
              <TabsTrigger value="cripto" className="px-2 text-xs sm:px-3 sm:text-sm">Cripto</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 rounded-lg border">
          <div className="flex flex-row items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-foreground">Efectivo en caja (teórico)</h3>
            <BanknotesIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold text-foreground">{formatCurrency(cashSummary?.efectivoTeorico ?? 0)}</div>
          <div className="mt-3 text-xs text-muted-foreground space-y-1">
            <div className="flex items-center justify-between">
              <span>
                Fondo inicial
                {isAdmin && (
                  <button
                    type="button"
                    onClick={editFixedFund}
                    className="ml-2 underline underline-offset-2 hover:text-foreground"
                  >
                    editar fijo
                  </button>
                )}
              </span>
              <span>{formatCurrency(cashSummary?.fondoInicial ?? cashSummary?.fondoFijo ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>+ Ventas en efectivo</span>
              <span>{formatCurrency(cashSummary?.ventasEfectivo ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>+ Cobranzas cta cte en efectivo</span>
              <span>{formatCurrency(cashSummary?.cobranzasEfectivo ?? 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>− Egresos en efectivo</span>
              <span>{formatCurrency(cashSummary?.egresosEfectivo ?? 0)}</span>
            </div>
          </div>
        </div>
        <div className="p-6 rounded-lg border">
          <div className="flex flex-row items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-medium text-foreground">Ventas del Periodo</h3>
              <p className="text-xs text-muted-foreground">(incluye Cta Cte)</p>
            </div>
            <DocumentTextIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold text-foreground">{formatCurrency(stats?.salesTodayTotal || 0)}</div>
        </div>
      </div>

      {/* Control de caja — sigue al día seleccionado en el filtro */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-foreground">
            Control de caja{!viewingToday && isSingleDay ? ` — ${arqueoDayLabel}` : ''}
          </h2>
        </div>
        {!isSingleDay ? (
          <div className="p-6 rounded-lg border text-sm text-muted-foreground">
            El arqueo de caja es por día. Elegí <span className="font-medium text-foreground">Hoy</span>,{' '}
            <span className="font-medium text-foreground">Ayer</span> o un día puntual en Personalizado para ver su cierre.
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-lg border">
            <div className="flex flex-row items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-foreground">Efectivo teórico ({arqueoDayLabel})</h3>
              <BanknotesIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(arqueoTeorico)}</div>
            <p className="text-xs text-muted-foreground">
              {arqueoClosure ? 'Congelado al momento del cierre' : 'Fondo + ventas ef. + cobranzas ef. − egresos ef.'}
            </p>
          </div>
          <div className="p-6 rounded-lg border">
            <div className="flex flex-row items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-foreground">Caja real</h3>
              <BuildingStorefrontIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-foreground">
              {arqueoClosure ? formatCurrency(arqueoClosure.countedCash) : 'Sin cierre'}
            </div>
            <p className="text-xs text-muted-foreground">
              {arqueoClosure && arqueoClosure.remainingCash != null
                ? `Retiro: ${formatCurrency(arqueoClosure.withdrawnCash || 0)} · Quedó en cajón: ${formatCurrency(arqueoClosure.remainingCash)}`
                : 'Efectivo contado físicamente'}
            </p>
          </div>
          <div className="p-6 rounded-lg border">
            <div className="flex flex-row items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-foreground">Diferencia</h3>
              <ClockIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-foreground">
              {difference === null ? 'Sin cierre' : formatCurrency(difference)}
            </div>
            <span className={cn("mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", getDifferenceBadge(difference))}>
              {difference === null ? 'Pendiente' : difference === 0 ? 'Cierra' : difference < 0 ? 'Falta' : 'Sobra'}
            </span>
            {arqueoClosure?.notes && (
              <p className="mt-2 text-xs text-muted-foreground truncate">Nota: {arqueoClosure.notes}</p>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Pendientes que impactan en caja */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-foreground">Pendientes que impactan en caja</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 rounded-lg border">
            <div className="flex flex-row items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-foreground">Cuentas corrientes</h3>
              <UsersIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(pendingSummary.currentAccounts.total)}</div>
            <p className="text-xs text-muted-foreground">{pendingSummary.currentAccounts.count} clientes con deuda</p>
          </div>
          <div className="p-6 rounded-lg border">
            <div className="flex flex-row items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-foreground">Cheques en cartera</h3>
              <DocumentTextIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(pendingSummary.checks.total)}</div>
            <p className="text-xs text-muted-foreground">{pendingSummary.checks.count} cheques pendientes</p>
          </div>
          <div className="p-6 rounded-lg border">
            <div className="flex flex-row items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-foreground">Remitos no cobrados</h3>
              <DocumentTextIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(pendingSummary.remitosUnpaid.total)}</div>
            <p className="text-xs text-muted-foreground">{pendingSummary.remitosUnpaid.count} remitos emitidos</p>
          </div>
        </div>
      </div>

      {/* Ingresos y Egresos del Periodo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ingresos */}
        <div className="p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-foreground">Ingresos ({rangeType === 'today' ? 'Hoy' : rangeType === 'yesterday' ? 'Ayer' : rangeType === 'thisWeek' ? 'Semana' : rangeType === 'thisMonth' ? 'Mes' : 'Personalizado'})</h3>
          <div className="md:hidden space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Efectivo</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.cash || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Tarjeta Credito</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.creditCard || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Tarjeta Debito</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.debitCard || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Transferencia</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.transfer || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Cheque</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.check || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">QR</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.qr || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Cta Cte</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.currentAccount || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Cripto</span>
              <span className="font-semibold">{formatCurrency(stats?.todayIncome.crypto || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm border-t pt-2 font-semibold text-green-600 dark:text-green-400">
              <span>Total ingresos a caja</span>
              <span>{formatCurrency(stats?.todayIncome.total || 0)}</span>
            </div>
          </div>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metodo</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Efectivo</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.cash || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Tarjeta Credito</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.creditCard || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Tarjeta Debito</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.debitCard || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Transferencia</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.transfer || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Cheque</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.check || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">QR</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.qr || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Cta Cte</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.currentAccount || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Cripto</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayIncome.crypto || 0)}
                  </TableCell>
                </TableRow>
                <TableRow className="border-t-2 font-bold">
                  <TableCell>Total Ingresos a Caja</TableCell>
                  <TableCell className="text-right text-green-600 dark:text-green-400">
                    {formatCurrency(stats?.todayIncome.total || 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Egresos */}
        <div className="p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-foreground">Egresos</h3>
          <div className="md:hidden space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Proveedores</span>
              <span className="font-semibold">{formatCurrency(stats?.todayExpenses.providers || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Gastos operativos</span>
              <span className="font-semibold">{formatCurrency(stats?.todayExpenses.operating || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Salarios</span>
              <span className="font-semibold">{formatCurrency(stats?.todayExpenses.salaries || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Impuestos</span>
              <span className="font-semibold">{formatCurrency(stats?.todayExpenses.taxes || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Retiros del dueno</span>
              <span className="font-semibold">{formatCurrency(stats?.todayExpenses.ownerWithdrawals || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Otros</span>
              <span className="font-semibold">{formatCurrency(stats?.todayExpenses.other || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm border-t pt-2 font-semibold text-red-600 dark:text-red-400">
              <span>Total egresos</span>
              <span>{formatCurrency(stats?.todayExpenses.total || 0)}</span>
            </div>
          </div>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Proveedores</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayExpenses.providers || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Gastos Operativos</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayExpenses.operating || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Salarios</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayExpenses.salaries || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Impuestos</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayExpenses.taxes || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Retiros del dueno</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayExpenses.ownerWithdrawals || 0)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Otros Gastos</TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(stats?.todayExpenses.other || 0)}
                  </TableCell>
                </TableRow>
                <TableRow className="border-t-2 font-bold">
                  <TableCell>Total Egresos</TableCell>
                  <TableCell className="text-right text-red-600 dark:text-red-400">
                    {formatCurrency(stats?.todayExpenses.total || 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Comparación de períodos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Comparación semanal</h3>
            <CalendarDaysIcon className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Esta semana:</span>
              <span className="font-semibold">{formatCurrency(stats?.weeklyComparison.thisWeek || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Semana anterior:</span>
              <span className="font-semibold">{formatCurrency(stats?.weeklyComparison.lastWeek || 0)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-muted-foreground">Crecimiento:</span>
              <div className="flex items-center">
                {(stats?.weeklyComparison.growth || 0) >= 0 ? (
                  <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                ) : (
                  <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                )}
                <Badge variant={(stats?.weeklyComparison.growth || 0) >= 0 ? "default" : "destructive"}>
                  {Math.abs(stats?.weeklyComparison.growth || 0).toFixed(1)}%
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Comparación mensual</h3>
            <CalendarDaysIcon className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Este mes:</span>
              <span className="font-semibold">{formatCurrency(stats?.monthlyComparison.thisMonth || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mes anterior:</span>
              <span className="font-semibold">{formatCurrency(stats?.monthlyComparison.lastMonth || 0)}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-muted-foreground">Crecimiento:</span>
              <div className="flex items-center">
                {(stats?.monthlyComparison.growth || 0) >= 0 ? (
                  <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                ) : (
                  <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                )}
                <Badge variant={(stats?.monthlyComparison.growth || 0) >= 0 ? "default" : "destructive"}>
                  {Math.abs(stats?.monthlyComparison.growth || 0).toFixed(1)}%
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Movimientos del Dia */}
      <div className="p-6 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4 text-foreground">Ultimos Movimientos</h3>
        <div>
          {movements.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay movimientos registrados</p>
            </div>
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {movements.map((movement) => {
                  const Icon = getPaymentMethodIcon(movement.methodKey);
                  const colorClass = getPaymentMethodColor(movement.methodKey);
                  return (
                    <div key={movement.id} className="rounded-lg border bg-card/80 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={cn("p-2 rounded-lg", colorClass)}>
                            <Icon className="h-4 w-4 text-white" />
                          </div>
                          <span className="font-medium">{movement.method}</span>
                        </div>
                        <span className={cn("text-sm font-semibold", movement.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                          {movement.type === 'income' ? '+' : '-'}{formatCurrency(movement.amount)}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">{movement.description}</div>
                      <div className="text-xs text-muted-foreground">{movement.time}</div>
                    </div>
                  );
                })}
              </div>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metodo</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Hora</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movements.map((movement) => {
                      const Icon = getPaymentMethodIcon(movement.methodKey);
                      const colorClass = getPaymentMethodColor(movement.methodKey);
                      
                      return (
                        <TableRow key={movement.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={cn("p-2 rounded-lg", colorClass)}>
                                <Icon className="h-4 w-4 text-white" />
                              </div>
                              <span className="font-medium">{movement.method}</span>
                            </div>
                          </TableCell>
                          <TableCell>{movement.description}</TableCell>
                          <TableCell className="text-muted-foreground">{movement.time}</TableCell>
                          <TableCell className={cn("text-right font-bold", movement.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                            {movement.type === 'income' ? '+' : '-'}{formatCurrency(movement.amount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de Registro de Egresos */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Egreso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Tipo de Egreso */}
            <div className="space-y-2">
              <Label htmlFor="expense-type">Tipo de Egreso</Label>
              <SearchableSelect
                id="expense-type"
                value={expenseForm.type}
                onChange={(value) => setExpenseForm(prev => ({ ...prev, type: value }))}
                placeholder="Buscar categoría..."
                options={EXPENSE_TYPE_OPTIONS}
              />
          </div>

            {/* Monto */}
            <div className="space-y-2">
              <Label htmlFor="expense-amount">Monto *</Label>
              <Input
                id="expense-amount"
                type="number"
                step="0.01"
                min="0"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
              />
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="expense-description">Tipo de gasto / Descripción *</Label>
              <SearchableSelect
                id="expense-description"
                creatable
                value={expenseForm.description}
                onChange={(value) => setExpenseForm(prev => ({ ...prev, description: value }))}
                placeholder="Elegí o escribí (ej: Combustible, Comida...)"
                options={EXPENSE_CONCEPT_OPTIONS}
              />
            </div>

            {/* Metodo de Pago */}
            <div className="space-y-2">
              <Label htmlFor="expense-method">Metodo de Pago</Label>
              <Select
                value={expenseForm.method}
                onValueChange={(value) => setExpenseForm(prev => ({
                  ...prev,
                  method: value,
                  // Si el nuevo medio no sale de una cuenta propia, limpiar la cuenta elegida
                  accountId: EXPENSE_NEEDS_ACCOUNT.includes(value) ? prev.accountId : ''
                }))}
              >
                <SelectTrigger id="expense-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="tarjeta_debito">Tarjeta Debito</SelectItem>
                  <SelectItem value="tarjeta_credito">Tarjeta Credito</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="qr">QR</SelectItem>
                  <SelectItem value="cuenta_corriente">Cta Cte</SelectItem>
                  <SelectItem value="cripto">Cripto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cuenta propia de la que sale la plata (solo medios electrónicos) */}
            {EXPENSE_NEEDS_ACCOUNT.includes(expenseForm.method) && financialAccounts.filter(a => a.kind !== 'efectivo').length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="expense-account">¿De qué cuenta salió?</Label>
                <Select
                  value={expenseForm.accountId || undefined}
                  onValueChange={(value) => setExpenseForm(prev => ({ ...prev, accountId: value }))}
                >
                  <SelectTrigger id="expense-account">
                    <SelectValue placeholder="Seleccionar cuenta..." />
                  </SelectTrigger>
                  <SelectContent>
                    {financialAccounts.filter(a => a.kind !== 'efectivo').map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowExpenseModal(false)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] h-9 px-4 py-2"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleRegisterExpense}
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] h-9 px-4 py-2 ${
                isDarkMode 
                  ? 'bg-white text-gray-900 hover:bg-gray-100' 
                  : 'bg-black text-white hover:bg-black/90'
              }`}
            >
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de cierre de caja */}
      <Dialog open={showCloseModal} onOpenChange={setShowCloseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cerrar caja</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border p-3 text-sm text-muted-foreground">
              Efectivo teórico (hoy): <span className="font-semibold text-foreground">{formatCurrency(theoreticalCash)}</span>
              <p className="mt-1 text-xs">Contá solo el efectivo del cajón. Tarjetas, transferencias y QR se concilian aparte.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="counted-cash">Efectivo contado</Label>
              <Input
                id="counted-cash"
                type="number"
                step="0.01"
                min="0"
                value={countedCash}
                onChange={(e) => setCountedCash(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="rounded-lg border p-3 text-sm">
              Diferencia: <span
                className={cn(
                  "font-semibold",
                  Number.isNaN(Number(countedCash))
                    ? "text-muted-foreground"
                    : Number(countedCash || 0) - theoreticalCash === 0
                      ? "text-emerald-500"
                      : "text-red-500"
                )}
              >
                {Number.isNaN(Number(countedCash)) ? 'n/a' : formatCurrency(Number(countedCash || 0) - theoreticalCash)}
              </span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="withdrawn-cash">Retiro de cierre (lo que sacás del cajón)</Label>
              <Input
                id="withdrawn-cash"
                type="number"
                step="0.01"
                min="0"
                value={withdrawnCash}
                onChange={(e) => setWithdrawnCash(e.target.value)}
                placeholder="0.00"
              />
              {(() => {
                const contado = Number(countedCash || 0);
                const fondoFijoCfg = Number(todaySummary?.fondoFijo ?? 0);
                const sugerido = Math.max(0, contado - fondoFijoCfg);
                const retiro = withdrawnCash === '' ? 0 : Number(withdrawnCash);
                const queda = Number.isNaN(retiro) ? null : contado - retiro;
                return (
                  <div className="text-xs text-muted-foreground space-y-1">
                    {contado > 0 && fondoFijoCfg > 0 && (
                      <div>
                        Sugerido para dejar el fondo fijo ({formatCurrency(fondoFijoCfg)}):{' '}
                        <button
                          type="button"
                          className="underline underline-offset-2 hover:text-foreground"
                          onClick={() => setWithdrawnCash(String(sugerido))}
                        >
                          {formatCurrency(sugerido)}
                        </button>
                      </div>
                    )}
                    <div>
                      Queda en el cajón: <span className="font-semibold text-foreground">{queda === null ? 'n/a' : formatCurrency(queda)}</span>
                      {' '}— mañana el efectivo teórico arranca de ahí.
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="space-y-2">
              <Label htmlFor="close-notes">Notas (opcional)</Label>
              <Input
                id="close-notes"
                type="text"
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Observaciones del cierre"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCloseModal(false)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] h-9 px-4 py-2"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCloseCash}
              disabled={closingCash}
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] h-9 px-4 py-2 ${
                isDarkMode
                  ? 'bg-white text-gray-900 hover:bg-gray-100'
                  : 'bg-black text-white hover:bg-black/90'
              }`}
            >
              {closingCash ? 'Cerrando...' : 'Confirmar cierre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashRegister;



